import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Users, Eye, Activity, TrendingUp, Plus, Trash2, Download, GitCompare,
  Calendar as CalIcon, Sparkles, Trophy, Hash, Lightbulb, WifiOff, Link,
} from 'lucide-react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

type Range = '7d' | '30d' | '90d' | 'custom';

interface Channel {
  id: string;
  title: string;
  username?: string;
}

interface StatsResponse {
  subscribers: { value: number; delta: number };
  reach: { value: number; delta: number };
  avgEr: { value: number; delta: number };
  viewsPerPost: { value: number; delta: number };
  series: { date: string; subs: number }[];
}

interface ForecastPoint { date: string; subs: number; forecast?: number }

interface ForecastResponse {
  points: ForecastPoint[];
}

interface InsightsResponse {
  bestDay: string;
  bestFormat: string;
  bestTopic: string;
  nextPostRecommendation: string;
}

interface Competitor {
  channelId: string;
  title: string;
  subscribers: number;
  growth30d: number;
  er: number;
  recentPosts: number;
}

interface RoiCampaign {
  id: string;
  name: string;
  invest: number;
  return: number;
  roi: number;
}

const RANGES: { key: Range; label: string }[] = [
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: 'custom', label: 'Custom' },
];

function formatDelta(d: number): string {
  const sign = d > 0 ? '+' : '';
  return `${sign}${d.toFixed(1)}%`;
}

function isServiceDown(error: unknown): boolean {
  if (!error) return false;
  const status = (error as { response?: { status?: number } }).response?.status;
  return status === 502 || status === 503 || status === 504;
}

function isNoData(error: unknown): boolean {
  if (!error) return false;
  const status = (error as { response?: { status?: number } }).response?.status;
  return status === 404;
}

function WaitingCard({ message }: { message: string }): JSX.Element {
  return (
    <div className="card flex flex-col items-center justify-center py-12 text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center">
        <CalIcon size={24} className="text-brand-primary opacity-40" />
      </div>
      <div className="text-brand-muted text-sm">{message}</div>
    </div>
  );
}

function OfflineBanner({ message }: { message: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 py-6 justify-center text-brand-muted text-sm">
      <WifiOff size={16} className="text-red-400" />
      <span>{message}</span>
    </div>
  );
}

function KpiCard(props: {
  title: string;
  value: string | number;
  delta?: number;
  icon: JSX.Element;
}): JSX.Element {
  const positive = (props.delta ?? 0) >= 0;
  return (
    <div className="card flex-1 min-w-[180px]">
      <div className="flex items-start justify-between mb-2">
        <div className="text-sm text-brand-muted">{props.title}</div>
        <div className="text-brand-primary">{props.icon}</div>
      </div>
      <div className="text-2xl font-bold text-brand-ink">{props.value}</div>
      {typeof props.delta === 'number' && (
        <div className={`text-xs mt-1 ${positive ? 'text-green-600' : 'text-red-600'}`}>
          {formatDelta(props.delta)} vs prev
        </div>
      )}
    </div>
  );
}

function KpiCardSkeleton(): JSX.Element {
  return (
    <div className="card flex-1 min-w-[180px] animate-pulse">
      <div className="h-3 bg-slate-200 rounded w-2/3 mb-3" />
      <div className="h-7 bg-slate-200 rounded w-1/2 mb-2" />
      <div className="h-2 bg-slate-200 rounded w-1/3" />
    </div>
  );
}

export function Analytics(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [channelId, setChannelId] = useState<string>('');
  const [range, setRange] = useState<Range>('30d');
  const [competitorIds, setCompetitorIds] = useState<string[]>(['comp-a', 'comp-b']);
  const [newCompetitor, setNewCompetitor] = useState('');

  // Load channels list
  const channels = useQuery<{ items: Channel[] }>({
    queryKey: ['channels'],
    queryFn: async () => {
      const { data } = await api.get<{ items: Channel[] }>('/api/channels');
      return data;
    },
  });

  // Auto-select first channel when loaded
  useEffect(() => {
    if (!channelId && channels.data?.items?.length) {
      setChannelId(channels.data.items[0].id);
    }
  }, [channels.data, channelId]);

  const hasChannels = (channels.data?.items?.length ?? 0) > 0;
  const activeChannelId = channelId || channels.data?.items?.[0]?.id || '';

  const { from, to } = useMemo(() => {
    const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
    return {
      from: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
    };
  }, [range]);

  const stats = useQuery<StatsResponse>({
    queryKey: ['analytics', 'stats', activeChannelId, from, to],
    queryFn: async () => {
      const { data } = await api.get<StatsResponse>(
        `/api/analytics/${activeChannelId}/stats`,
        { params: { from, to } },
      );
      return data;
    },
    enabled: !!activeChannelId,
    retry: (_, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 404 && status !== 502 && status !== 503;
    },
  });

  const forecast = useQuery<ForecastResponse>({
    queryKey: ['analytics', 'forecast', activeChannelId],
    queryFn: async () => {
      const { data } = await api.get<ForecastResponse>(
        `/api/analytics/${activeChannelId}/forecast`,
        { params: { days: 30 } },
      );
      return data;
    },
    enabled: !!activeChannelId,
    retry: (_, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 404 && status !== 502 && status !== 503;
    },
  });

  const insights = useQuery<InsightsResponse>({
    queryKey: ['analytics', 'insights', activeChannelId],
    queryFn: async () => {
      const { data } = await api.get<InsightsResponse>(
        `/api/analytics/${activeChannelId}/insights`,
      );
      return data;
    },
    enabled: !!activeChannelId,
    retry: (_, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 404 && status !== 502 && status !== 503;
    },
  });

  const competitors = useQuery<{ items: Competitor[] }>({
    queryKey: ['analytics', 'competitors', competitorIds],
    queryFn: async () => {
      const { data } = await api.get<{ items: Competitor[] }>(
        `/api/analytics/competitors`,
        { params: { channel_ids: competitorIds.join(',') } },
      );
      return data;
    },
    enabled: competitorIds.length > 0,
    retry: (_, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 502 && status !== 503;
    },
  });

  const roi = useQuery<{ items: RoiCampaign[] }>({
    queryKey: ['analytics', 'roi', activeChannelId],
    queryFn: async () => {
      const { data } = await api.get<{ items: RoiCampaign[] }>(
        `/api/analytics/${activeChannelId}/roi`,
      );
      return data;
    },
    enabled: user?.plan === 'agency' && !!activeChannelId,
    retry: (_, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 502 && status !== 503;
    },
  });

  const exportPdf = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ url: string }>(
        `/api/analytics/${activeChannelId}/export`,
        { from, to },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success('PDF готов');
      if (data?.url) window.open(data.url, '_blank');
    },
    onError: (error: unknown) => {
      if (isServiceDown(error)) {
        toast.error('Export unavailable — service offline');
      } else {
        toast.error('Ошибка экспорта');
      }
    },
  });

  const addCompetitor = (): void => {
    const trimmed = newCompetitor.trim();
    if (!trimmed) return;
    if (competitorIds.length >= 5) {
      toast.error('Максимум 5 конкурентов');
      return;
    }
    if (competitorIds.includes(trimmed)) {
      toast.error('Уже добавлен');
      return;
    }
    setCompetitorIds([...competitorIds, trimmed]);
    setNewCompetitor('');
  };

  const removeCompetitor = (id: string): void => {
    setCompetitorIds(competitorIds.filter((c) => c !== id));
  };

  const chartData = useMemo(() => {
    const past = (stats.data?.series ?? []).map((p) => ({
      date: p.date, actual: p.subs, forecast: null as number | null,
    }));
    const fc = (forecast.data?.points ?? []).map((p) => ({
      date: p.date, actual: null as number | null, forecast: p.forecast ?? p.subs,
    }));
    return [...past, ...fc];
  }, [stats.data, forecast.data]);

  const statsNoData = stats.isError && (isNoData(stats.error) || isServiceDown(stats.error));
  const forecastNoData = forecast.isError && (isNoData(forecast.error) || isServiceDown(forecast.error));
  const insightsNoData = insights.isError && (isNoData(insights.error) || isServiceDown(insights.error));
  const competitorsOffline = competitors.isError && isServiceDown(competitors.error);
  const roiOffline = roi.isError && isServiceDown(roi.error);

  // No channels connected
  if (!channels.isLoading && !hasChannels) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="analytics-screen">
        <div className="card max-w-sm w-full flex flex-col items-center text-center gap-4 py-12">
          <div className="w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center">
            <Link size={28} className="text-brand-primary opacity-50" />
          </div>
          <h2 className="text-lg font-semibold text-brand-ink">Connect a channel first</h2>
          <p className="text-brand-muted text-sm">
            Connect a Telegram channel to start tracking analytics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="analytics-screen">
      {/* Topbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-brand-ink">{t('nav.analytics')}</h1>
          <select
            aria-label="channel-selector"
            value={activeChannelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="input"
          >
            {channels.isLoading && (
              <option value="">Loading...</option>
            )}
            {channels.data?.items.map((ch) => (
              <option key={ch.id} value={ch.id}>
                {ch.username ? `@${ch.username}` : ch.title}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border rounded-lg p-1">
            <CalIcon size={16} className="text-brand-muted ml-2" />
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1 rounded-md text-sm ${
                  range === r.key ? 'bg-brand-primary text-white' : 'text-brand-muted'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => toast('Добавление конкурента — см. ниже')}
          >
            <GitCompare size={16} /> Compare
          </button>
          <button
            className="btn-primary flex items-center gap-2"
            onClick={() => exportPdf.mutate()}
            disabled={exportPdf.isPending || !activeChannelId}
          >
            <Download size={16} /> Export PDF
          </button>
        </div>
      </div>

      {/* Hero KPI row */}
      {stats.isLoading && (
        <div className="flex flex-wrap gap-4" data-testid="kpi-row-skeleton">
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
        </div>
      )}

      {statsNoData && !stats.isLoading && (
        <WaitingCard message="Waiting for data..." />
      )}

      {stats.data && (
        <div className="flex flex-wrap gap-4" data-testid="kpi-row">
          <KpiCard
            title="Подписчики"
            value={stats.data.subscribers.value.toLocaleString()}
            delta={stats.data.subscribers.delta}
            icon={<Users size={20} />}
          />
          <KpiCard
            title="Охват"
            value={stats.data.reach.value.toLocaleString()}
            delta={stats.data.reach.delta}
            icon={<Eye size={20} />}
          />
          <KpiCard
            title="Avg ER"
            value={`${stats.data.avgEr.value.toFixed(2)}%`}
            delta={stats.data.avgEr.delta}
            icon={<Activity size={20} />}
          />
          <KpiCard
            title="Views per post"
            value={stats.data.viewsPerPost.value.toLocaleString()}
            delta={stats.data.viewsPerPost.delta}
            icon={<TrendingUp size={20} />}
          />
        </div>
      )}

      {/* Subscribers chart */}
      <div className="card" data-testid="subscribers-chart">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-brand-ink">Динамика подписчиков</h2>
          <div className="text-xs text-brand-muted">Прогноз 30 дней (пунктир)</div>
        </div>
        {(stats.isLoading || forecast.isLoading) && (
          <div className="animate-pulse">
            <div className="h-[300px] bg-slate-100 rounded-lg" />
          </div>
        )}
        {(statsNoData || forecastNoData) && !stats.isLoading && !forecast.isLoading && chartData.length === 0 && (
          <div className="py-12 text-center">
            <div className="text-brand-muted text-sm">Waiting for data...</div>
          </div>
        )}
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="actual"
                name="Факт"
                stroke="#0088CC"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="forecast"
                name="Прогноз"
                stroke="#0088CC"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Competitors */}
      <div className="card" data-testid="competitors-table">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-brand-ink">Конкуренты</h2>
          <div className="flex items-center gap-2">
            <input
              aria-label="new-competitor"
              className="input"
              placeholder="@channel"
              value={newCompetitor}
              onChange={(e) => setNewCompetitor(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addCompetitor(); }}
            />
            <button
              className="btn-primary flex items-center gap-1"
              onClick={addCompetitor}
              aria-label="add-competitor"
            >
              <Plus size={16} /> Добавить
            </button>
          </div>
        </div>
        {competitorsOffline ? (
          <OfflineBanner message="Competitor analysis offline" />
        ) : competitors.data?.items.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-brand-muted border-b">
                <th className="py-2">Канал</th>
                <th className="py-2">Подписчики</th>
                <th className="py-2">Рост 30d</th>
                <th className="py-2">ER</th>
                <th className="py-2">Постов</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {competitors.data.items.map((c) => (
                <tr key={c.channelId} className="border-b last:border-0">
                  <td className="py-2">{c.title}</td>
                  <td className="py-2">{c.subscribers.toLocaleString()}</td>
                  <td className={`py-2 ${c.growth30d >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatDelta(c.growth30d)}
                  </td>
                  <td className="py-2">{c.er.toFixed(2)}%</td>
                  <td className="py-2">{c.recentPosts}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => removeCompetitor(c.channelId)}
                      aria-label={`remove-${c.channelId}`}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-brand-muted text-center py-6">
            {competitors.isLoading ? t('common.loading') : 'Нет конкурентов. Добавьте до 5 каналов.'}
          </div>
        )}
      </div>

      {/* AI Insights */}
      <div>
        <h2 className="text-lg font-semibold text-brand-ink mb-3 flex items-center gap-2">
          <Sparkles size={18} className="text-brand-primary" /> AI Insights
        </h2>
        {insightsNoData && !insights.isLoading ? (
          <WaitingCard message="Waiting for data..." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="insights-panel">
            <div className="card">
              <div className="flex items-center gap-2 text-brand-muted text-sm mb-2">
                <CalIcon size={16} /> Лучший день
              </div>
              <div className="text-lg font-semibold text-brand-ink">
                {insights.isLoading ? (
                  <div className="h-5 bg-slate-200 rounded w-16 animate-pulse" />
                ) : (
                  insights.data?.bestDay ?? '—'
                )}
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 text-brand-muted text-sm mb-2">
                <Trophy size={16} /> Лучший формат
              </div>
              <div className="text-lg font-semibold text-brand-ink">
                {insights.isLoading ? (
                  <div className="h-5 bg-slate-200 rounded w-20 animate-pulse" />
                ) : (
                  insights.data?.bestFormat ?? '—'
                )}
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 text-brand-muted text-sm mb-2">
                <Hash size={16} /> Лучшая тематика
              </div>
              <div className="text-lg font-semibold text-brand-ink">
                {insights.isLoading ? (
                  <div className="h-5 bg-slate-200 rounded w-24 animate-pulse" />
                ) : (
                  insights.data?.bestTopic ?? '—'
                )}
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 text-brand-muted text-sm mb-2">
                <Lightbulb size={16} /> Рекомендация
              </div>
              <div className="text-sm text-brand-ink">
                {insights.isLoading ? (
                  <div className="h-5 bg-slate-200 rounded w-32 animate-pulse" />
                ) : (
                  insights.data?.nextPostRecommendation ?? '—'
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ROI tracker — Agency tier */}
      {user?.plan === 'agency' && (
        <div className="card" data-testid="roi-tracker">
          <h2 className="text-lg font-semibold text-brand-ink mb-3">ROI Tracker</h2>
          {roiOffline ? (
            <OfflineBanner message="ROI tracking offline" />
          ) : roi.data?.items.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-muted border-b">
                  <th className="py-2">Кампания</th>
                  <th className="py-2">Invest</th>
                  <th className="py-2">Return</th>
                  <th className="py-2">ROI %</th>
                </tr>
              </thead>
              <tbody>
                {roi.data.items.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2">{c.name}</td>
                    <td className="py-2">${c.invest.toLocaleString()}</td>
                    <td className="py-2">${c.return.toLocaleString()}</td>
                    <td className={`py-2 ${c.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatDelta(c.roi)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-brand-muted text-center py-6">
              {roi.isLoading ? t('common.loading') : 'Нет активных кампаний'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
